import React, { useCallback } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import { useArtisanStaging } from '../hooks/useArtisanStaging';
import { ArtisanDropzone } from './ArtisanDropzone';
import { ArtisanFileTabs } from './ArtisanFileTabs';
import { ArtisanStagedSheet } from './ArtisanStagedSheet';
import { ArtisanUploadResults } from './ArtisanUploadResults';

interface Props {
    canWrite: boolean;
    onBack: () => void;
    onOpenProfile: (profileId: string) => void;
}

/**
 * The upload workspace. Files are read and reviewed in the browser first; nothing reaches
 * the database until the roaster saves. One file and twenty follow the same path — the tab
 * strip is what makes the second case reviewable, and the tab strip is also where more
 * files are added to a batch already under review.
 */
export const ArtisanUploadPanel: React.FC<Props> = ({ canWrite, onBack, onOpenProfile }) => {
    const { t } = useLanguage();
    const stage = useArtisanStaging();

    const { readProgress, saveProgress, items, activeId, saveAll, reset } = stage;
    const isReading = readProgress !== null;
    const isSaving = saveProgress !== null;
    const isLocked = isReading || isSaving;

    const active = items.find(i => i.id === activeId) ?? null;

    const handleSave = useCallback(async () => {
        const finished = await saveAll();
        // A single roast that landed cleanly is more use opened than summarised.
        const only = finished.length === 1 ? finished[0] : null;
        if (only?.status === 'saved' && only.profileId) {
            reset();
            onOpenProfile(only.profileId);
        }
    }, [saveAll, reset, onOpenProfile]);

    const progress = readProgress ?? saveProgress;
    const progressLabel = readProgress
        ? t('artisanSync.stage.reading', { done: readProgress.done, total: readProgress.total })
        : saveProgress
            ? t('artisanSync.bulk.uploading', { done: saveProgress.done, total: saveProgress.total })
            : '';

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="flex items-center gap-4 min-w-0">
                    <button
                        onClick={() => {
                            reset();
                            onBack();
                        }}
                        disabled={isLocked}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                        data-test-id="artisan-upload-back"
                    >
                        <ChevronLeft className="h-5 w-5 text-gray-600" />
                    </button>
                    <div className="min-w-0">
                        <h1 className="text-2xl font-semibold text-gray-900">{t('artisanSync.upload.title')}</h1>
                        <p className="text-sm text-gray-500 mt-1">{t('artisanSync.upload.subtitle')}</p>
                    </div>
                </div>

                {items.length > 0 && !stage.hasSaved && (
                    <div className="flex items-center gap-3 sm:ml-auto sm:flex-shrink-0">
                        <button
                            onClick={reset}
                            disabled={isLocked}
                            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                            data-test-id="artisan-discard-batch"
                        >
                            {t('artisanSync.stage.discardAll')}
                        </button>
                        {canWrite && (
                            <button
                                onClick={handleSave}
                                disabled={isLocked || stage.readyCount === 0}
                                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                data-test-id="artisan-save-batch"
                            >
                                {isSaving
                                    ? t('artisanSync.upload.saving')
                                    : stage.readyCount === 1
                                        ? t('artisanSync.upload.saveToDb')
                                        : t('artisanSync.stage.saveAll', { count: stage.readyCount })}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {progress && (
                <div className="space-y-2">
                    <p className="text-sm text-gray-500">{progressLabel}</p>
                    <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary-600 transition-all duration-300"
                            style={{ width: `${progress.total === 0 ? 0 : (progress.done / progress.total) * 100}%` }}
                        />
                    </div>
                </div>
            )}

            {items.length === 0 ? (
                <ArtisanDropzone onFilesPicked={stage.addFiles} disabled={isLocked} />
            ) : stage.hasSaved ? (
                <ArtisanUploadResults
                    items={items}
                    onSelectProfile={onOpenProfile}
                    onUploadMore={reset}
                    onDone={() => {
                        reset();
                        onBack();
                    }}
                />
            ) : (
                // No gap between the strip and the sheet: the active tab has to meet the
                // card it belongs to, or the folder-tab reading falls apart.
                <div>
                    <ArtisanFileTabs
                        tabs={items.map(i => ({ id: i.id, fileName: i.fileName, status: i.status }))}
                        activeId={activeId}
                        onSelect={stage.setActiveId}
                        onFilesPicked={stage.addFiles}
                        disabled={isLocked}
                    />
                    {active && (
                        // Keyed so the profile editor starts clean on every tab rather than
                        // carrying the previous roast's rows into the next one.
                        <ArtisanStagedSheet
                            key={active.id}
                            item={active}
                            canWrite={canWrite}
                            isLocked={isLocked}
                            onEdit={rows => stage.setEdited(active.id, rows)}
                            onRemove={() => stage.removeItem(active.id)}
                            onSelectProfile={onOpenProfile}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
